import { taxonomyApi } from '@/modules/catalog/services/taxonomy-api';
import { describeTaxonomyFailure } from '@/modules/catalog/services/taxonomy-failures';
import { toError } from '@/modules/ai';
import type { CategoryAttributeInput } from '@/api/schemas/catalog-taxonomy';
import type {
  AttributeDraft,
  CategoryDraft,
  CreatedCategory,
  SaveOutcome,
} from '@/modules/catalog/pages/setup-page/types';
import { kindNeedsOptions, parseOptions } from '@/modules/catalog/pages/setup-page/parts/drafts';

/**
 * SAVING ONE ACCEPTED ITEM.
 *
 * ---------------------------------------------------------------------------
 * ONE REQUEST PER ITEM, AND THE ITEM OWNS ITS KEY
 * ---------------------------------------------------------------------------
 * Every write below carries the idempotency key the DRAFT holds, so a retry of
 * that item is that item again and never a second record. There is no
 * "save everything" call: the catalogue API is asked for one record at a time,
 * and each of them is asked in its own right.
 *
 * ---------------------------------------------------------------------------
 * NOTHING IS INVENTED
 * ---------------------------------------------------------------------------
 * The bodies carry only what the catalogue API accepts. No price, no stock
 * level, no certification, no country, no currency — the catalogue API has no
 * member for any of them, and a body with one would be refused.
 */
export type CategorySaveResult = {
  readonly outcome: SaveOutcome;
  /** The category this page created, when it was created. */
  readonly created: CreatedCategory | null;
};

export type AttributeSaveResult = {
  readonly outcome: SaveOutcome;
  /** The attribute set the category should now hold, when it was written. */
  readonly assigned: ReadonlyArray<CategoryAttributeInput> | null;
  /** The category's next draft version, when it was written. */
  readonly draftVersion: number | null;
};

/**
 * Creates one accepted category.
 *
 * A category this page creates starts its draft at 1 — it has never had its
 * attribute set written — and the API answers every later write with the next
 * version, which is the only version this page ever sends.
 */
export async function saveCategoryDraft(draft: CategoryDraft): Promise<CategorySaveResult> {
  try {
    const category = await taxonomyApi.createCategory(
      {
        name: draft.name.trim(),
        slug: draft.slug.trim(),
        description: draft.description.trim() === '' ? null : draft.description,
        ...(draft.parentCategoryId === '' ? {} : { parentCategoryId: draft.parentCategoryId }),
      },
      draft.idempotencyKey,
    );
    return {
      outcome: { status: 'created', message: `Created “${category.name}”.` },
      created: {
        categoryId: category.categoryId,
        name: category.name,
        draftVersion: 1,
        assigned: [],
      },
    };
  } catch (cause) {
    return {
      outcome: { status: 'refused', message: describeTaxonomyFailure(toError(cause)) },
      created: null,
    };
  }
}

/**
 * Creates one accepted attribute definition, and attaches it when it can.
 *
 * The definition is written first, because a definition without an attachment
 * is still a useful record; the attachment is a second request against the
 * category, and it is made only for a category this page created.
 */
export async function saveAttributeDraft(
  draft: AttributeDraft,
  target: CreatedCategory | null,
  targetLabel: string | null,
): Promise<AttributeSaveResult> {
  const key = draft.key.trim();
  const options = kindNeedsOptions(draft.kind) ? parseOptions(draft.optionsText) : [];

  try {
    await taxonomyApi.createAttributeDefinition(
      {
        attributeKey: key,
        kind: draft.kind,
        name: draft.name.trim(),
        ...(options.length === 0 ? {} : { options: [...options] }),
      },
      draft.idempotencyKey,
    );
  } catch (cause) {
    return {
      outcome: { status: 'refused', message: describeTaxonomyFailure(toError(cause)) },
      assigned: null,
      draftVersion: null,
    };
  }

  if (target === null) {
    return {
      outcome: { status: 'created', message: unattachedMessage(draft, targetLabel) },
      assigned: null,
      draftVersion: null,
    };
  }

  const next: ReadonlyArray<CategoryAttributeInput> = [
    ...target.assigned,
    { attributeKey: key, required: false, variantAxis: draft.variantAxis, sortOrder: target.assigned.length },
  ];

  try {
    const result = await taxonomyApi.setCategoryAttributes(
      target.categoryId,
      next,
      target.draftVersion,
    );
    return {
      outcome: {
        status: 'created',
        message: `Created the definition for “${draft.name}” and attached it to “${target.name}”.`,
      },
      assigned: next,
      draftVersion: result.draftVersion,
    };
  } catch (cause) {
    return {
      outcome: {
        status: 'created-not-assigned',
        message: `Created the definition for “${draft.name}”, but it was not attached to “${target.name}”: ${describeTaxonomyFailure(toError(cause))}`,
      },
      assigned: null,
      draftVersion: null,
    };
  }
}

/**
 * Why a definition was not attached.
 *
 * An EXISTING category is never written from this page: the attribute set is a
 * full-set replace, and the API exposes no read of a category's current
 * attributes, so replacing one could destroy assignments this page cannot see.
 */
function unattachedMessage(draft: AttributeDraft, targetLabel: string | null): string {
  if (targetLabel === null) {
    return `Created the definition for “${draft.name}”. It is not attached to a category.`;
  }
  return `Created the definition for “${draft.name}”. It is not attached to “${targetLabel}”: the API exposes no read of a category's current attributes, so replacing that set could destroy them.`;
}
